import type { Algorithm } from '../types'
import { runPipeline as runMock, type RunHandle, type RunHandlers } from './mockRun'
import { tokenManager } from '../../services/api'

// Real remote execution: streams SSE events from the merged Cubemate backend
// (/api/mlmate). Uses fetch-based streaming instead of EventSource because the
// endpoint is JWT-gated and EventSource cannot send an Authorization header —
// same pattern as the chat/capstone streamers in services/api.ts.
// If the backend is unreachable, falls back to the in-browser simulation so
// the UI always works (e.g. frontend-only dev).

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

export function runPipelineRemote(
  algo: Algorithm,
  inputs: Record<string, number | string>,
  handlers: RunHandlers,
): RunHandle {
  const url = `${API_BASE_URL}/api/mlmate/run/${algo.id}?inputs=${encodeURIComponent(JSON.stringify(inputs))}`
  const controller = new AbortController()
  let gotAny = false
  let fallback: RunHandle | null = null

  const startFallback = () => {
    if (gotAny || fallback) return
    handlers.onLog({
      stageId: algo.pipeline[0].id,
      kind: 'info',
      text: '⚠ backend offline — running simulated demo (start the FastAPI server for real execution)',
    })
    fallback = runMock(algo, inputs, handlers)
  }

  const dispatch = (event: string, data: string) => {
    gotAny = true
    if (event === 'stage') {
      const d = JSON.parse(data)
      handlers.onStageStatus(d.stageId, d.status)
    } else if (event === 'log') {
      handlers.onLog(JSON.parse(data))
    } else if (event === 'result') {
      handlers.onResult(JSON.parse(data))
    } else if (event === 'done') {
      handlers.onDone()
    }
  }

  ;(async () => {
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
        signal: controller.signal,
      })
      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`)

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = '' // SSE blocks can split across network chunks — always buffer

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const blocks = buffer.split('\n\n')
        buffer = blocks.pop() ?? '' // last piece may be incomplete — keep it
        for (const block of blocks) {
          let event = ''
          let data = ''
          for (const line of block.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim()
            else if (line.startsWith('data:')) data = line.slice(5).trim()
          }
          if (event && data) dispatch(event, data)
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return // user cancelled — not an error
      startFallback()
    }
  })()

  return {
    cancel: () => {
      controller.abort()
      fallback?.cancel()
    },
  }
}
